import React, { useState } from 'react';

import { Card, CardSection } from '@lightspeed/flame/Card';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { FormHelperProps } from '@lightspeed/flame/FormField';
import { Text } from '@lightspeed/flame/Text';
import axios from 'axios';

import Format from 'root/apps/helpers/format';

import { UppercaseSubHeading } from '../../../settings/MAS/SubscriptionSetup/index.style';
import { B2B_CALL_ROOT, buildImportMSRP, flashError, flashSuccess, varElem } from '../utils';
import { DuplicateDialog, ImageCount, ImageDisplay, Label, ValuesGrid } from './styles';
import { B2BImportItem, DuplicateDialogProps, ImageProps } from './types';

const NO_IMAGE_URL = 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/b2b/no_image_100.svg';
const PROPOSED_DESC_PROPS: FormHelperProps = { css: { marginLeft: '28px' } };

function CurrentImage({ item }: ImageProps) {
  const imageUrl = item.image_url || NO_IMAGE_URL;
  const additionalCount = item.image_count - 1;
  return (
    <ImageDisplay>
      <img alt="Current" className="b2b-import-current-image" src={imageUrl} />
      {additionalCount > 0 && (
        <ImageCount className="b2b-import-additional-image-count">
          + <var>additionalCount</var> images
        </ImageCount>
      )}
    </ImageDisplay>
  );
}

export function B2BDuplicateDialog({
  b2bItem,
  b2bMsrp,
  onCancel,
  onConfirm,
}: DuplicateDialogProps) {
  const b2bMsrpLabel = b2bMsrp ? Format.toMoney(b2bMsrp.amount, b2bMsrp.currency) : 'N/A';
  const b2bImage = b2bItem.images?.[0];
  const existingImageCount = b2bItem.inventory_item_details?.image_count || 0;
  const disableImage = !b2bImage || existingImageCount >= 12;

  const [imageChecked, setImageChecked] = useState(!disableImage);
  const [nameChecked, setNameChecked] = useState(true);
  const [msrpChecked, setMsrpChecked] = useState(!!b2bMsrp);
  const [brandChecked, setBrandChecked] = useState(true);

  const handleConfirmImportClick = () => {
    doUpdate();
    onConfirm();
  };

  const doUpdate = async () => {
    const existingItemId = b2bItem.inventory_item_details?.item_id;
    const update = `${B2B_CALL_ROOT}/${existingItemId}`;
    const body: B2BImportItem = { b2b_catalog_uuid: b2bItem.id, upc: b2bItem.upc };

    const msrpImport = buildImportMSRP(b2bMsrp);
    if (msrpImport && msrpChecked) {
      body.msrp = msrpImport;
    }

    if (nameChecked) {
      body.description = b2bItem.name;
    }

    if (brandChecked) {
      body.brand_name = b2bItem.brand_name;
    }

    if (imageChecked && b2bItem.images?.length) {
      body.image_url = b2bItem.images[0].image_url;
    }

    try {
      await axios.post(update, body);
      flashSuccess();
    } catch {
      flashError();
    }
  };

  // The Dialog actually takes a react node, but it's typed as string
  // So we have to trick it.
  // Only show UPC if we have one
  const title: string = b2bItem.upc
    ? ((
        <span>
          Duplicate detected for UPC &apos;<var>{b2bItem.upc}</var>&apos;
        </span>
      ) as any)
    : ((<span>Duplicate detected</span>) as any);

  return (
    <DuplicateDialog
      isOpen={true}
      title={title}
      onConfirm={handleConfirmImportClick}
      onCancel={onCancel}
      confirmText="Import"
      type="secondary"
      message={
        <>
          <Text fontSize="14px" mt="-10px">
            This item already exists in your inventory. Importing this product will update any
            existing information. Select the fields to update for this product.
          </Text>
          <ValuesGrid>
            <Card style={{ display: 'grid' }}>
              <CardSection
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <UppercaseSubHeading textTransform="uppercase" pb="4px">
                    current values
                  </UppercaseSubHeading>
                  <Label description={<CurrentImage item={b2bItem.inventory_item_details!} />}>
                    Image
                  </Label>
                  <Label
                    description={varElem(b2bItem.inventory_item_details?.description, ' ', true)}
                    pt="4px"
                  >
                    Item
                  </Label>
                </div>
                <div>
                  <Label description={varElem(b2bItem.inventory_item_details?.msrp, 'N/A')}>
                    MSRP
                  </Label>
                  <Label description={varElem(b2bItem.inventory_item_details?.brand_name, 'N/A')}>
                    Brand
                  </Label>
                </div>
              </CardSection>
            </Card>
            <Card style={{ display: 'grid' }}>
              <CardSection
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <UppercaseSubHeading textTransform="uppercase" pb="4px">
                    proposed values
                  </UppercaseSubHeading>
                  <Label
                    description={
                      <ImageDisplay>
                        <img src={b2bImage?.image_url || NO_IMAGE_URL} alt="Proposed" />
                      </ImageDisplay>
                    }
                  >
                    <Checkbox
                      checked={imageChecked}
                      onChange={(e) => setImageChecked(e.target.checked)}
                      disabled={disableImage}
                      data-test="b2b-item-image"
                    />
                    Image
                  </Label>
                  <Label
                    descriptionProps={PROPOSED_DESC_PROPS}
                    description={varElem(b2bItem.name, 'N/A', true)}
                    pt="7px"
                  >
                    <Checkbox
                      checked={nameChecked}
                      onChange={(e) => setNameChecked(e.target.checked)}
                      data-test="b2b-item-name"
                    />
                    Item
                  </Label>
                </div>
                <div>
                  <Label
                    descriptionProps={PROPOSED_DESC_PROPS}
                    description={varElem(b2bMsrpLabel, 'N/A')}
                  >
                    <Checkbox
                      checked={msrpChecked}
                      onChange={(e) => setMsrpChecked(e.target.checked)}
                      disabled={!b2bMsrp}
                    />
                    MSRP
                  </Label>
                  <Label
                    descriptionProps={PROPOSED_DESC_PROPS}
                    description={varElem(b2bItem.brand_name, 'N/A')}
                  >
                    <Checkbox
                      checked={brandChecked}
                      onChange={(e) => setBrandChecked(e.target.checked)}
                    />
                    Brand
                  </Label>
                </div>
              </CardSection>
            </Card>
          </ValuesGrid>
        </>
      }
    />
  );
}
